// Multi light plots — the small client-side plumbing shared by the Lights
// config page (upload + thumbnails) and the rider PDF builders (resolve the
// referenced files to embeddable data URLs).
//
// DESIGN (2026-10-06): the lights config stores ONLY small references
// (`LightsConfig.plots: LightPlotRef[]`) to files held in the band file store
// (`band_files`, category 'light_plot'). The binary NEVER enters the config
// blob; the rider PDF resolves each reference to a PNG/JPEG data URL through
// the guarded server preview route at build time.
//
// SHARED LIST (2026-10-06, evolution B): the light-plot list is SHARED across
// the 4 transport modes. It is READ as the UNION of the 4 modes' lists
// (deduplicated by fileId, order van → plane → tourbus → train, each mode's own
// order preserved) and WRITTEN IDENTICALLY into all 4 modes so no normalisation
// or other screen can drop it. Nothing is ever deleted — the union only grows.
//
// MULTI-PAGE (2026-10-06, evolution A): a PDF light plot renders EVERY page (up
// to the shared safety cap), one image per page; an image is a single render.
import { apiUploadBandFile, apiUploadBandFileRaw, BAND_FILE_RAW_THRESHOLD, apiBandFilePreviewUrl, apiBandFilePreviewPages, type BandFileDTO } from '../api';
import type { LightsConfig, LightPlotRef, LightPlotRender, TransportType } from '../types';

export { apiBandFilePreviewUrl as lightPlotPreviewUrl };
export { apiBandFilePreviewPages as lightPlotPreviewPages };

/** Canonical transport order for the shared light-plot list. Mirrors the
 *  `MODE_KEYS` order used by `normalizeAppConfig`. */
export const LIGHT_PLOT_TRANSPORTS: TransportType[] = ['van', 'plane', 'tourbus', 'train'];

/** Safety cap shared with the server (`MAX_PREVIEW_PAGES` in band-files.js):
 *  the resolver never requests more than this many pages of one PDF, so a
 *  many-hundred-page PDF can never blow up the rider docDef. */
export const MAX_PREVIEW_PAGES = 20;

/**
 * The SHARED light-plot list: the UNION of the 4 transport modes, deduplicated
 * by `fileId`, in stable order (van → plane → tourbus → train, each mode's own
 * order preserved). The UI AND the rider PDF read this single source. (2026-10-06)
 */
export function sharedLightPlots(
  lights: Partial<Record<TransportType, LightsConfig>> | undefined | null,
): LightPlotRef[] {
  const out: LightPlotRef[] = [];
  const seen = new Set<number>();
  if (!lights) return out;
  for (const transport of LIGHT_PLOT_TRANSPORTS) {
    const list = lights[transport]?.plots;
    if (!Array.isArray(list)) continue;
    for (const ref of list) {
      if (!ref || ref.fileId == null) continue;
      const key = Number(ref.fileId);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(ref);
    }
  }
  return out;
}

/**
 * Twin the shared list IDENTICALLY into all 4 transport modes (a soft migration:
 * on load the union differs; at the first save all 4 converge). Returns a NEW
 * lights map — every mode keeps its other fields, only `plots` is replaced.
 * Nothing is ever deleted by this step. (2026-10-06)
 */
export function withSharedLightPlots(
  lights: Partial<Record<TransportType, LightsConfig>>,
  plots: LightPlotRef[],
): Record<TransportType, LightsConfig> {
  const next = {} as Record<TransportType, LightsConfig>;
  for (const transport of LIGHT_PLOT_TRANSPORTS) {
    next[transport] = { ...(lights?.[transport] as LightsConfig), plots: plots.map((p) => ({ ...p })) };
  }
  return next;
}

/** Read a File as a raw base64 payload (no data-URL prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || '');
      const i = s.indexOf(',');
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}

/** Upload one light-plot file to the band store (category 'light_plot') and
 *  return the tiny reference to persist in the lights config. Editor+ on the
 *  band is required server-side; a member delegated `content.rider` is allowed
 *  for this category ONLY (see server/routes/band-files.js). */
export async function uploadLightPlot(bandId: string, file: File): Promise<LightPlotRef> {
  // >20 MB: switch automatically to the RAW route (same server cap, no base64).
  const dto: BandFileDTO = file.size > BAND_FILE_RAW_THRESHOLD
    ? await apiUploadBandFileRaw(bandId, file, { category: 'light_plot' })
    : await apiUploadBandFile(bandId, {
        filename: file.name,
        mime: file.type || '',
        base64: await fileToBase64(file),
        category: 'light_plot',
      });
  return { id: crypto.randomUUID(), fileId: dto.id, name: dto.filename, mime: dto.mime || file.type || '' };
}

/** Re-encode a non-PNG/JPEG data URL to PNG via a canvas (pdfmake under Node
 *  only embeds PNG/JPEG). Returns undefined on failure. */
async function toPngDataUrl(dataUrl: string): Promise<string | undefined> {
  return new Promise((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => resolve(undefined), 8000);
    img.onload = () => {
      clearTimeout(timer);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(undefined);
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(undefined);
      }
    };
    img.onerror = () => { clearTimeout(timer); resolve(undefined); };
    img.src = dataUrl;
  });
}

/** Fetch ONE page of a light plot from the guarded preview route and return it
 *  as a pdfmake-safe data URL (PNG/JPEG passthrough; anything else re-encoded
 *  to PNG). Also returns `pages`, the PDF total read off `X-Pdf-Pages` (1 for
 *  an image, 1 on failure). Returns `{ pages }` with no dataUrl on any failure. */
async function previewPageToDataUrl(
  bandId: string,
  fileId: number,
  page: number,
): Promise<{ dataUrl?: string; pages: number }> {
  try {
    const res = await fetch(apiBandFilePreviewUrl(bandId, fileId, page), { credentials: 'include' });
    const raw = Number.parseInt(res.headers.get('X-Pdf-Pages') || '', 10);
    const pages = Number.isFinite(raw) && raw > 0 ? raw : 1;
    if (!res.ok) return { pages };
    const blob = await res.blob();
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('read failed'));
      reader.readAsDataURL(blob);
    });
    if (dataUrl.startsWith('data:image/png') || dataUrl.startsWith('data:image/jpeg')) return { dataUrl, pages };
    return { dataUrl: await toPngDataUrl(dataUrl), pages };
  } catch {
    return { pages: 1 };
  }
}

/** Resolve the ordered light-plot list to embeddable data URLs, ONE per PDF
 *  page. The array order is preserved (failed pages dropped); for a PDF the
 *  caption becomes `"<name> — page i/N"` when the PDF has more than one page
 *  (N = the true total, at most MAX_PREVIEW_PAGES rendered). An image is a
 *  single render with the plain name. */
export async function resolveLightPlotsForPdf(
  bandId: string | null | undefined,
  plots: LightPlotRef[] | undefined,
): Promise<LightPlotRender[]> {
  if (!bandId || !Array.isArray(plots) || plots.length === 0) return [];
  const out: LightPlotRender[] = [];
  for (const ref of plots) {
    if (!ref || ref.fileId == null) continue;
    const fileId = Number(ref.fileId);
    const first = await previewPageToDataUrl(bandId, fileId, 1);
    if (!first.dataUrl) continue;
    const total = first.pages > 0 ? first.pages : 1;
    const rendered = Math.min(total, MAX_PREVIEW_PAGES); // never request beyond the cap
    const caption = (page: number): string | undefined => {
      if (!ref.name) return total > 1 ? `page ${page}/${total}` : undefined;
      return total > 1 ? `${ref.name} — page ${page}/${total}` : ref.name;
    };
    out.push({ name: caption(1), dataUrl: first.dataUrl });
    if (rendered > 1) {
      const rest = await Promise.all(
        Array.from({ length: rendered - 1 }, (_, i) => previewPageToDataUrl(bandId, fileId, i + 2)),
      );
      rest.forEach((r, i) => {
        if (r.dataUrl) out.push({ name: caption(i + 2), dataUrl: r.dataUrl });
      });
    }
  }
  return out;
}